import React, { useState, useEffect, useRef } from 'react';
import { coreAPI, MAX_VIDEO_SIZE, uploadVideoFile } from '../../services/api';
import { useOrganization } from '../../context/OrganizationContext';
import { LoadingPage } from '../../components/Loading';
import { useToast } from '../../context/ToastContext';
import RichTextEditor from '../../components/RichTextEditor';
import './Admin.css';

const VIDEO_ACCEPT = 'video/mp4,video/webm,video/ogg,.mp4,.webm,.ogg';

const emptyForm = {
  chinese_name: '',
  english_name: '',
  title: '',
  district: '',
  constituency: '',
  office_address: '',
  phone_numbers: '',
  email: '',
  office_hours: '',
  structure_subtitle: '',
  about_content: '',
  facebook_url: '',
  instagram_url: '',
  primary_color: '#5dade2',
  home_hero_subtitle: '',
  home_mission_title: '',
  home_mission_text: '',
  home_mission_tagline: '',
  home_services_intro: '',
  home_work_scopes: '',
  home_future_title: '',
  home_future_text: '',
  home_cta_title: '',
  home_cta_text: '',
};

const SiteSettings = () => {
  const [form, setForm] = useState(emptyForm);
  const [videoUrl, setVideoUrl] = useState('');
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const videoInputRef = useRef(null);
  const { success, error } = useToast();
  const { refetch } = useOrganization();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await coreAPI.adminGetOrganization();
      const { home_hero_video, ...rest } = response.data;
      setForm({ ...emptyForm, ...rest });
      setVideoUrl(home_hero_video || '');
    } catch (err) {
      error('無法載入網站設定');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRichChange = (name) => (value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleVideoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/') && !/\.(mp4|webm|ogg)$/i.test(file.name)) {
      error('請選擇影片檔案（MP4、WebM 或 OGG）');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_VIDEO_SIZE) {
      error('影片大小不能超過 300MB');
      e.target.value = '';
      return;
    }

    setUploadingVideo(true);
    setUploadProgress(0);
    try {
      const response = await uploadVideoFile(file, 'hero', (event) => {
        if (event.total) {
          setUploadProgress(Math.round((event.loaded * 100) / event.total));
        }
      });
      setVideoUrl(response.home_hero_video || response.url);
      await refetch();
      success('介紹影片已上傳');
    } catch (err) {
      error(err.response?.data?.error || err.message || '影片上傳失敗');
      console.error(err);
    } finally {
      setUploadingVideo(false);
      setUploadProgress(0);
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    }
  };

  const handleVideoRemove = async () => {
    setUploadingVideo(true);
    try {
      await coreAPI.adminDeleteHeroVideo();
      setVideoUrl('');
      await refetch();
      success('介紹影片已移除');
    } catch (err) {
      error('移除影片失敗');
      console.error(err);
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      Object.keys(emptyForm).forEach((key) => {
        payload[key] = form[key] ?? '';
      });
      await coreAPI.adminUpdateOrganization(payload);
      await refetch();
      success('網站設定已更新');
    } catch (err) {
      error('更新失敗');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingPage message="載入網站設定中..." />;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>網站設定</h1>
        <p>管理首頁內容、聯絡我們、組織架構及全站基本資訊</p>
      </div>

      <form className="admin-form card" onSubmit={handleSubmit}>
        <h2 className="form-section-title">基本資訊</h2>
        <div className="form-row">
          <div className="form-group">
            <label>中文名稱</label>
            <input name="chinese_name" value={form.chinese_name || ''} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>英文名稱</label>
            <input name="english_name" value={form.english_name || ''} onChange={handleChange} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>首頁主標題</label>
            <input name="title" value={form.title || ''} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>地區</label>
            <input name="district" value={form.district || ''} onChange={handleChange} />
          </div>
        </div>

        <div className="form-group">
          <label>組織架構副標題</label>
          <input name="structure_subtitle" value={form.structure_subtitle || ''} onChange={handleChange} />
        </div>

        <h2 className="form-section-title">首頁介紹影片</h2>
        <p className="form-section-hint">顯示於首頁最上方，訪客進入網站時自動播放。未上傳時會使用網站預設介紹影片。建議 MP4，檔案小於 50MB 以加快載入。</p>
        <div className="form-group video-upload-group">
          {videoUrl ? (
            <video className="hero-video-preview" src={videoUrl} controls playsInline />
          ) : (
            <div className="hero-video-preview-empty">尚未上傳介紹影片</div>
          )}
          <div className="photo-upload-controls">
            <input
              ref={videoInputRef}
              type="file"
              accept={VIDEO_ACCEPT}
              onChange={handleVideoSelect}
              className="photo-file-input"
              disabled={uploadingVideo}
            />
            {uploadingVideo && (
              <p className="form-hint">
                {uploadProgress > 0 ? `上傳中… ${uploadProgress}%` : '處理中…'}
              </p>
            )}
            {videoUrl && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleVideoRemove}
                disabled={uploadingVideo}
              >
                移除影片
              </button>
            )}
            <p className="form-hint">支援 MP4、WebM、OGG，最大 300MB</p>
          </div>
        </div>

        <h2 className="form-section-title">首頁內容</h2>
        <p className="form-section-hint">支援富文本編輯，可插入圖片、連結及格式化文字</p>

        <RichTextEditor
          label="首頁副標題"
          value={form.home_hero_subtitle}
          onChange={handleRichChange('home_hero_subtitle')}
          placeholder="輸入首頁副標題，可使用工具列插入圖片..."
          minHeight={200}
        />

        <div className="form-group">
          <label>使命標題</label>
          <input name="home_mission_title" value={form.home_mission_title || ''} onChange={handleChange} />
        </div>

        <RichTextEditor
          label="使命內容"
          value={form.home_mission_text}
          onChange={handleRichChange('home_mission_text')}
          placeholder="輸入使命內容..."
          minHeight={200}
        />

        <div className="form-group">
          <label>使命標語</label>
          <input name="home_mission_tagline" value={form.home_mission_tagline || ''} onChange={handleChange} />
        </div>

        <RichTextEditor
          label="核心服務簡介"
          value={form.home_services_intro}
          onChange={handleRichChange('home_services_intro')}
          placeholder="輸入核心服務簡介..."
          minHeight={120}
        />

        <RichTextEditor
          label="工作範疇"
          value={form.home_work_scopes}
          onChange={handleRichChange('home_work_scopes')}
          placeholder="可使用列表或段落描述工作範疇..."
          minHeight={180}
        />

        <div className="form-row">
          <div className="form-group">
            <label>未來展望標題</label>
            <input name="home_future_title" value={form.home_future_title || ''} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>行動呼籲標題</label>
            <input name="home_cta_title" value={form.home_cta_title || ''} onChange={handleChange} />
          </div>
        </div>

        <RichTextEditor
          label="未來展望內容"
          value={form.home_future_text}
          onChange={handleRichChange('home_future_text')}
          placeholder="輸入未來展望內容..."
          minHeight={150}
        />

        <RichTextEditor
          label="行動呼籲內容"
          value={form.home_cta_text}
          onChange={handleRichChange('home_cta_text')}
          placeholder="輸入行動呼籲內容..."
          minHeight={150}
        />

        <h2 className="form-section-title">聯絡我們</h2>
        <div className="form-group">
          <label>辦公室地址</label>
          <textarea name="office_address" value={form.office_address || ''} onChange={handleChange} rows={3} />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>電話號碼 (逗號分隔)</label>
            <input name="phone_numbers" value={form.phone_numbers || ''} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>電郵</label>
            <input name="email" type="email" value={form.email || ''} onChange={handleChange} />
          </div>
        </div>

        <div className="form-group">
          <label>辦公時間</label>
          <input name="office_hours" value={form.office_hours || ''} onChange={handleChange} />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Facebook</label>
            <input name="facebook_url" value={form.facebook_url || ''} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Instagram</label>
            <input name="instagram_url" value={form.instagram_url || ''} onChange={handleChange} />
          </div>
        </div>

        <h2 className="form-section-title">關於我們</h2>
        <RichTextEditor
          label="組織介紹"
          value={form.about_content}
          onChange={handleRichChange('about_content')}
          placeholder="輸入組織介紹..."
          minHeight={250}
        />

        <div className="form-group">
          <label>主題顏色</label>
          <input name="primary_color" type="color" value={form.primary_color || '#5dade2'} onChange={handleChange} />
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? '儲存中...' : '儲存設定'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SiteSettings;
